// Per-member group role payload + wire codec (Signal GroupsV2 parity).
//
// Mirrors iOS GroupRole.swift exactly.
//
// Threat model: a hostile server (or an evicted ex-member with a saved
// envelope) must NOT be able to forge or replay a role change.
// Defences:
//   1. Outer pairwise envelope is Ed25519-signed by the actor's identity
//      key (handled by E2EEManager), proving WHO sent it.
//   2. Inner HMAC-SHA256 under the group master key proves the actor
//      was a current member at send time. After a remove-member event
//      the master key rotates, so a kicked peer's old signed envelopes
//      cannot have their inner HMAC recomputed against the new key.
//   3. Authorisation matrix and monotonicity live in GroupRoleStore.
//
// Wire format of the *plaintext that gets encrypted*:
//   kaether:role:v1:<base64url(JSON{ "payload": <inner-json>,
//                                    "groupSig": "<base64url 32-byte HMAC>" })>
//
// The inner HMAC is computed over the canonical-bytes form of the
// payload (`canonicalBytes`), NOT over the JSON encoding, so a server
// that re-pretty-prints the JSON cannot break MAC verification.
// Canonical bytes:
//   schema || groupId || targetUserId || newRole || prevRoleClaim
//          || actorUserId || ts
// Joined with the ASCII Unit-Separator byte 0x1F. `ts` is appended as
// 8 little-endian bytes.
//
// DEPENDENCY (C.3): signWithGroupKey / verifyWithGroupKey are imported
// from ../group-identity/GroupIdentity. When C.3 merges from its parallel
// worktree, the stub there becomes the full implementation. No changes
// needed here — the import path is stable.

import {
  GroupMasterKey,
  signWithGroupKey,
  verifyWithGroupKey,
  base64UrlEncode,
  base64UrlDecode,
} from "../group-identity/GroupIdentity";

// Re-export GroupMasterKey so callers of this module don't have to import
// two packages.
export { GroupMasterKey };

// ---------------------------------------------------------------------------
// GroupRole enum
// ---------------------------------------------------------------------------

/** The four role levels a group member can hold. */
export type GroupRole = "owner" | "admin" | "member" | "removed";

// ---------------------------------------------------------------------------
// RoleChangePayload value type
// ---------------------------------------------------------------------------

export interface RoleChangePayload {
  readonly schema: number;
  /** GroupPublicId.asString — base64url 16-byte derived id. */
  readonly groupId: string;
  readonly targetUserId: string;
  readonly newRole: GroupRole;
  /**
   * Actor's claim of the target's previous role. Advisory: surfaced
   * to UI as a concurrent-update warning if it disagrees with the
   * current store state, but does NOT cause a hard reject (Signal
   * uses last-writer-wins under quorum, no formal CRDT).
   */
  readonly prevRoleClaim: GroupRole;
  readonly actorUserId: string;
  /** Unix epoch seconds as a 53-bit safe integer. */
  readonly ts: number;
}

/** Current schema version. */
export const ROLE_CHANGE_SCHEMA = 1;

// ---------------------------------------------------------------------------
// Codec errors
// ---------------------------------------------------------------------------

export type RoleChangeCodecError =
  | { kind: "notARoleChange" }
  | { kind: "malformed"; reason: string }
  | { kind: "unsupportedSchema"; schema: number }
  | { kind: "invalidGroupSignature" };

// ---------------------------------------------------------------------------
// Wire envelope (internal to codec)
// ---------------------------------------------------------------------------

interface RoleChangeEnvelope {
  payload: RoleChangePayload;
  /** base64url-encoded 32-byte HMAC. */
  groupSig: string;
}

// ---------------------------------------------------------------------------
// RoleChangeCodec
// ---------------------------------------------------------------------------

const ENC = new TextEncoder();

export const ROLE_CHANGE_PREFIX = "kaether:role:v1:";

export const RoleChangeCodec = {
  prefix: ROLE_CHANGE_PREFIX,

  isRoleChange(s: string): boolean {
    return s.startsWith(ROLE_CHANGE_PREFIX);
  },

  /**
   * Encode + HMAC-sign. The signature covers `canonicalBytes(payload)`
   * rather than the JSON form, so receivers verify against bytes
   * they re-derive from the decoded struct — JSON whitespace and key
   * ordering are irrelevant.
   */
  async encode(
    payload: RoleChangePayload,
    masterKey: GroupMasterKey,
  ): Promise<string> {
    const canonical = canonicalBytes(payload);
    const sig = await signWithGroupKey(canonical, masterKey);
    const envelope: RoleChangeEnvelope = {
      payload,
      groupSig: base64UrlEncode(sig),
    };
    const outerBytes = ENC.encode(JSON.stringify(envelope));
    return ROLE_CHANGE_PREFIX + base64UrlEncode(outerBytes);
  },

  /**
   * Parse the envelope, decode the inner payload, then verify the HMAC
   * under `masterKey`. Throws a `RoleChangeCodecError` on tamper or wrong
   * key (constant-time check is inside `verifyWithGroupKey`).
   */
  async decode(
    s: string,
    masterKey: GroupMasterKey,
  ): Promise<RoleChangePayload> {
    if (!s.startsWith(ROLE_CHANGE_PREFIX)) {
      throw { kind: "notARoleChange" } satisfies RoleChangeCodecError;
    }
    const b64 = s.slice(ROLE_CHANGE_PREFIX.length);
    const outerBytes = base64UrlDecode(b64);
    if (!outerBytes) {
      throw {
        kind: "malformed",
        reason: "invalid base64url",
      } satisfies RoleChangeCodecError;
    }
    let envelope: RoleChangeEnvelope;
    try {
      envelope = JSON.parse(new TextDecoder().decode(outerBytes)) as RoleChangeEnvelope;
    } catch (e) {
      throw {
        kind: "malformed",
        reason: `json decode failed: ${e}`,
      } satisfies RoleChangeCodecError;
    }
    if (
      !envelope ||
      typeof envelope !== "object" ||
      !envelope.payload ||
      typeof envelope.groupSig !== "string"
    ) {
      throw {
        kind: "malformed",
        reason: "envelope missing payload or groupSig",
      } satisfies RoleChangeCodecError;
    }
    const p = envelope.payload;
    if (p.schema !== ROLE_CHANGE_SCHEMA) {
      throw {
        kind: "unsupportedSchema",
        schema: p.schema,
      } satisfies RoleChangeCodecError;
    }
    if (!p.groupId) {
      throw { kind: "malformed", reason: "empty groupId" } satisfies RoleChangeCodecError;
    }
    if (!p.targetUserId) {
      throw {
        kind: "malformed",
        reason: "empty targetUserId",
      } satisfies RoleChangeCodecError;
    }
    if (!p.actorUserId) {
      throw {
        kind: "malformed",
        reason: "empty actorUserId",
      } satisfies RoleChangeCodecError;
    }
    const sig = base64UrlDecode(envelope.groupSig);
    if (!sig) {
      throw {
        kind: "malformed",
        reason: "invalid groupSig base64url",
      } satisfies RoleChangeCodecError;
    }
    const canonical = canonicalBytes(p);
    const valid = await verifyWithGroupKey(canonical, sig, masterKey);
    if (!valid) {
      throw { kind: "invalidGroupSignature" } satisfies RoleChangeCodecError;
    }
    return p;
  },

  /**
   * Convenience: returns null for non-role-change plaintext. Throws only
   * for payloads that *did* claim to be role changes.
   */
  async tryDecode(
    s: string,
    masterKey: GroupMasterKey,
  ): Promise<RoleChangePayload | null> {
    if (!RoleChangeCodec.isRoleChange(s)) return null;
    return RoleChangeCodec.decode(s, masterKey);
  },
} as const;

// ---------------------------------------------------------------------------
// Canonical bytes (HMAC input)
// ---------------------------------------------------------------------------

/**
 * Deterministic byte representation, independent of JSON whitespace or key
 * ordering. Field separator is the ASCII Unit-Separator (0x1F) which cannot
 * appear inside any of the UTF-8 string fields since identifiers are
 * restricted to printable text upstream.
 *
 * Layout (mirrors iOS exactly):
 *   schema || 0x1F || groupId || 0x1F || targetUserId || 0x1F
 *          || newRole || 0x1F || prevRoleClaim || 0x1F
 *          || actorUserId || 0x1F || ts(8 bytes little-endian)
 */
export function canonicalBytes(p: RoleChangePayload): Uint8Array {
  const SEP = 0x1f;
  const parts: Array<Uint8Array | number> = [];

  function appendStr(s: string): void {
    parts.push(ENC.encode(s));
    parts.push(SEP);
  }

  appendStr(String(p.schema));
  appendStr(p.groupId);
  appendStr(p.targetUserId);
  appendStr(p.newRole);
  appendStr(p.prevRoleClaim);
  appendStr(p.actorUserId);

  // ts as 8 little-endian bytes (Int64). JS numbers are safe up to 2^53 so
  // we handle it as two 32-bit words. The high word is almost always 0 for
  // Unix timestamps in the current era.
  const tsLow = p.ts >>> 0;
  const tsHigh = Math.floor(p.ts / 0x1_0000_0000) >>> 0;
  const tsBytes = new Uint8Array(8);
  const view = new DataView(tsBytes.buffer);
  view.setUint32(0, tsLow, /* littleEndian */ true);
  view.setUint32(4, tsHigh, /* littleEndian */ true);
  parts.push(tsBytes);

  // Flatten
  let total = 0;
  for (const p of parts) {
    total += typeof p === "number" ? 1 : p.length;
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    if (typeof part === "number") {
      out[offset++] = part;
    } else {
      out.set(part, offset);
      offset += part.length;
    }
  }
  return out;
}
